import { logger } from "../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { Trophy, Medal, Award, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getRankBgColor = (rank) => {
  if (rank === 1) return 'bg-gold/20';
  if (rank === 2) return 'bg-gray-300/20';
  if (rank === 3) return 'bg-amber-600/20';
  return 'bg-white/10';
};

const getRankTextColor = (rank) => {
  if (rank === 1) return 'text-gold';
  if (rank === 2) return 'text-gray-300';
  if (rank === 3) return 'text-amber-600';
  return 'text-white/50';
};

export default function Leaderboard() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await axios.get(`${API}/leaderboard`);
        setEntries(response.data);
      } catch (err) {
        logger.error("Error fetching leaderboard:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, []);

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return <Trophy className="w-6 h-6 text-gold" />;
      case 2: return <Medal className="w-6 h-6 text-gray-300" />;
      case 3: return <Award className="w-6 h-6 text-amber-600" />;
      default: return <span className="text-white/40 font-heading text-lg">#{rank}</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-casino-black">
        <div className="text-gold animate-pulse font-heading text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="leaderboard-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-casino-black to-casino-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-gold rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-casino-red rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <p className="text-gold uppercase tracking-[0.3em] text-sm mb-6 font-accent">
            Top Players
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white mb-6">
            Leader<span className="gold-gradient">board</span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto">
            See who's dominating the tables at San Marcos Social Club.
          </p>
        </div>
      </section>

      {/* Top 3 Podium */}
      {entries.length >= 3 && (
        <section className="py-16 bg-casino-black">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-end justify-center gap-4">
              {/* 2nd Place */}
              <div className="text-center flex-1 max-w-[200px]" data-testid="podium-2nd">
                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-gray-300/20 to-gray-400/10 border-2 border-gray-300/30 flex items-center justify-center mb-4">
                  <Users className="w-10 h-10 text-gray-300" />
                </div>
                <h3 className="text-white font-heading text-lg mb-1">{entries[1]?.player_name}</h3>
                <p className="text-gray-300 font-semibold">{entries[1]?.points?.toLocaleString()} pts</p>
                <div className="mt-4 h-24 bg-gradient-to-t from-gray-500/20 to-transparent rounded-t-lg flex items-center justify-center">
                  <span className="text-3xl font-heading text-gray-300">2</span>
                </div>
              </div>

              {/* 1st Place */}
              <div className="text-center flex-1 max-w-[220px]" data-testid="podium-1st">
                <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-gold/30 to-gold/10 border-2 border-gold/50 flex items-center justify-center mb-4 animate-glow">
                  <Trophy className="w-12 h-12 text-gold" />
                </div>
                <h3 className="text-gold font-heading text-xl mb-1">{entries[0]?.player_name}</h3>
                <p className="text-gold font-semibold">{entries[0]?.points?.toLocaleString()} pts</p>
                <div className="mt-4 h-32 bg-gradient-to-t from-gold/20 to-transparent rounded-t-lg flex items-center justify-center">
                  <span className="text-4xl font-heading text-gold">1</span>
                </div>
              </div>

              {/* 3rd Place */}
              <div className="text-center flex-1 max-w-[200px]" data-testid="podium-3rd">
                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-amber-600/20 to-amber-700/10 border-2 border-amber-600/30 flex items-center justify-center mb-4">
                  <Users className="w-10 h-10 text-amber-600" />
                </div>
                <h3 className="text-white font-heading text-lg mb-1">{entries[2]?.player_name}</h3>
                <p className="text-amber-600 font-semibold">{entries[2]?.points?.toLocaleString()} pts</p>
                <div className="mt-4 h-16 bg-gradient-to-t from-amber-600/20 to-transparent rounded-t-lg flex items-center justify-center">
                  <span className="text-3xl font-heading text-amber-600">3</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Full Leaderboard Table */}
      <section className="py-20 bg-casino-dark" data-testid="leaderboard-table-section">
        <div className="max-w-5xl mx-auto px-4">
          <div className="glass-card overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <h2 className="font-heading text-2xl text-white">Full Rankings</h2>
            </div>
            
            {entries.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-16 h-16 text-gold/30 mx-auto mb-4" />
                <p className="text-white/60 text-lg">No players ranked yet. Join a tournament to get on the board!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 bg-black/30">
                      <th className="px-6 py-4 text-left text-xs uppercase tracking-wider text-gold">Rank</th>
                      <th className="px-6 py-4 text-left text-xs uppercase tracking-wider text-gold">Player</th>
                      <th className="px-6 py-4 text-right text-xs uppercase tracking-wider text-gold">Points</th>
                      <th className="px-6 py-4 text-right text-xs uppercase tracking-wider text-gold">Wins</th>
                      <th className="px-6 py-4 text-right text-xs uppercase tracking-wider text-gold hidden sm:table-cell">Tournament</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry, index) => (
                      <tr 
                        key={entry.id}
                        className={`border-b border-white/5 ${
                          entry.rank <= 3 ? 'bg-gold/5' : ''
                        }`}
                        data-testid={`leaderboard-row-${index}`}
                      >
                        <td className="px-6 py-5">
                          <div className="flex items-center justify-center w-10 h-10">
                            {getRankIcon(entry.rank)}
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getRankBgColor(entry.rank)}`}>
                              <Users className={`w-5 h-5 ${getRankTextColor(entry.rank)}`} />
                            </div>
                            <span className={`font-medium ${entry.rank <= 3 ? 'text-white' : 'text-white/80'}`}>
                              {entry.player_name}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-right">
                          <span className={`font-semibold ${entry.rank === 1 ? 'text-gold' : 'text-white'}`}>
                            {entry.points?.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-5 text-right">
                          <span className="text-gold">{entry.wins}</span>
                        </td>
                        <td className="px-6 py-5 text-right hidden sm:table-cell">
                          <span className="text-white/50 text-sm">{entry.tournament || '-'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
